// ── ResourceNotes (COLLAB-NOTIF Slice 2) ─────────────────────────────────────
// ONE component, two hosts. `host="pane"` is compact and scrolls internally at
// 260px (reading pane, Zone 3); `host="detail"` is full-width, shows avatars, and
// lets the page scroll. Ordering, states, permission behaviour and copy are
// identical — a rule fixed once is fixed everywhere.
//
// Flat chronological (oldest first), composer beneath, NO nesting. Add on
// CONFIRMATION (never optimistic): the note joins when the server confirms; on
// failure the text stays in the box (card 5). Permanent — no edit/delete.
// Author = display name, falling back to email, never "Unknown" (card 3).
// Absolute time only, one format both hosts. Read-only viewers get a lock line,
// not a disabled box (card 6). Two empty states, never an inviting empty above a
// lock (card 7).

import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthStore } from '@/store/auth.store';
import {
  useResourceNotes,
  useAddResourceNote,
  NOTE_ADD_ROLES,
  type ResourceNoteType,
  type ResourceNote,
} from '@/api/hooks/useResourceNotes';
import { cn } from '@/lib/utils';

interface ResourceNotesProps {
  resourceType: ResourceNoteType;
  resourceId: string;
  host: 'pane' | 'detail';
  /** Human label for the pane scope line, e.g. "risk response" → "on this risk response". */
  resourceLabel?: string;
}

// Author line (COLLAB-NOTIF polish): the display name LEADS at medium weight (readable, not heavy) with
// the email MUTED beside it; when there is no name the email leads and stays legible (not faded). The
// email is useful but must not dominate the note. No "Unknown".
function AuthorLine({ n, size }: { n: ResourceNote; size: 'sm' | 'xs' }) {
  const name = n.author_name && n.author_name.trim() ? n.author_name.trim() : null;
  const lead = size === 'sm' ? 'text-sm' : 'text-xs';
  const emailSize = size === 'sm' ? 'text-[12px]' : 'text-[11px]';
  if (name) {
    return (
      <>
        <span className={cn(lead, 'font-medium text-text-muted')}>{name}</span>
        {n.author_email && <span className={cn(emailSize, 'text-text-faint')}>{n.author_email}</span>}
      </>
    );
  }
  return <span className={cn(lead, 'text-text-secondary')}>{n.author_email ?? '—'}</span>;
}

function initialsFor(n: ResourceNote): string {
  const name = n.author_name && n.author_name.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
  }
  const local = (n.author_email ?? '?').split('@')[0];
  return local.slice(0, 2);
}

function NoteRow({ n, host }: { n: ResourceNote; host: 'pane' | 'detail' }) {
  const when = format(parseISO(n.created_at), 'd MMM, HH:mm');
  if (host === 'detail') {
    return (
      <div className="flex gap-3 border-b border-border-subtle px-4 py-3 last:border-0">
        <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full border border-border-subtle bg-bg-surface-2 text-[11px] font-semibold uppercase text-text-secondary">
          {initialsFor(n)}
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <AuthorLine n={n} size="sm" />
            <span className="text-[11px] tabular-nums text-text-faint">{when}</span>
          </div>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-text-secondary">{n.body}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1 border-b border-border-subtle px-3 py-2.5 last:border-0">
      <div className="flex flex-wrap items-baseline gap-2">
        <AuthorLine n={n} size="xs" />
        <span className="text-[10.5px] tabular-nums text-text-faint">{when}</span>
      </div>
      <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-text-secondary">{n.body}</p>
    </div>
  );
}

export function ResourceNotes({ resourceType, resourceId, host, resourceLabel = 'record' }: ResourceNotesProps) {
  const canAdd = useAuthStore((s) => s.hasAnyRole(NOTE_ADD_ROLES));
  const { data: notes, isLoading } = useResourceNotes(resourceType, resourceId);
  const add = useAddResourceNote(resourceType, resourceId);
  const [draft, setDraft] = useState('');

  const list = notes ?? [];
  const isEmpty = !isLoading && list.length === 0;
  const canSubmit = draft.trim().length > 0 && !add.isPending;

  const submit = () => {
    if (!canSubmit) return;
    add.mutate(draft, { onSuccess: () => setDraft('') }); // clear ONLY on confirm; keep on error
  };

  const detail = host === 'detail';

  return (
    <div className={cn('flex flex-col overflow-hidden rounded-lg border border-border-subtle bg-bg-surface-2/40')}>
      {/* Header — Notes + count (count hidden at 0 / loading; zero renders nothing) */}
      <div className="flex items-center gap-2 border-b border-border-subtle px-3 py-2">
        <span className={cn('font-semibold uppercase tracking-wide text-text-secondary', detail ? 'text-xs' : 'text-[10px]')}>
          Notes
        </span>
        {!isLoading && list.length > 0 && (
          <span className="rounded-full border border-accent-cyan/40 bg-accent-cyan/10 px-1.5 text-[10.5px] font-bold tabular-nums text-accent-cyan">
            {list.length}
          </span>
        )}
        <span className="ml-auto text-[10.5px] text-text-faint">on this {resourceLabel}</span>
      </div>

      {/* Body — loading / empty / list */}
      {isLoading ? (
        <div className="flex flex-col gap-3 p-3">
          <div className="flex flex-col gap-1.5">
            <Skeleton className="h-2 w-1/3" /><Skeleton className="h-2 w-5/6" /><Skeleton className="h-2 w-2/3" />
          </div>
          <div className="flex flex-col gap-1.5"><Skeleton className="h-2 w-1/4" /><Skeleton className="h-2 w-3/4" /></div>
        </div>
      ) : isEmpty ? (
        <div className="flex flex-col gap-1 px-3 py-4 text-center">
          <span className="text-[12.5px] font-semibold text-text-secondary">No notes recorded</span>
          {/* Per-context sub (COLLAB-NOTIF polish): compact pane + can-add = title only (the composer
              is the invite); detail + can-add = a short guiding line; read-only (both hosts) informs,
              never invites — nothing inviting ever sits above the lock line. */}
          {(!canAdd || detail) && (
            <span className="text-[11.5px] leading-snug text-text-faint">
              {canAdd
                ? 'Add a note to capture context for everyone with access.'
                : 'Nothing has been recorded for this item.'}
            </span>
          )}
        </div>
      ) : (
        <div className={cn('flex flex-col', !detail && 'max-h-[260px] overflow-y-auto')}>
          {list.map((n) => <NoteRow key={n.id} n={n} host={host} />)}
        </div>
      )}

      {/* Composer (can-add) OR read-only lock line — never a disabled box */}
      {canAdd ? (
        <div className="flex flex-col gap-2 border-t border-border-subtle bg-bg-surface px-3 py-2.5">
          <Textarea
            rows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write a note…"
            className="resize-none text-[12.5px]"
            aria-label="Write a note"
          />
          <div className="flex flex-wrap items-center gap-2.5">
            <Button size="sm" disabled={!canSubmit} onClick={submit}>
              {add.isPending ? 'Adding…' : 'Add note'}
            </Button>
            <span className="text-[10.5px] text-text-faint">Visible to everyone with access to this resource</span>
          </div>
          {add.isError && (
            <div className="rounded-md border border-severity-critical/40 bg-severity-critical/10 px-2.5 py-2">
              <span className="text-[11.5px] leading-snug text-severity-critical">
                Note could not be saved. Your text is still in the box — press Add note to try again.
              </span>
            </div>
          )}
          {detail && (
            <p className="border-t border-dashed border-border-subtle pt-2 text-[10.5px] text-text-faint">
              Notes are permanent — no edit or delete. To correct one, add another note.
            </p>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 border-t border-border-subtle bg-bg-surface px-3 py-2.5">
          <Lock className="h-3.5 w-3.5 flex-none text-text-faint" />
          <span className="text-[11.5px] leading-snug text-text-secondary">
            Your access is read-only — you can read notes but not add them.
          </span>
        </div>
      )}
    </div>
  );
}
